import React from "react";
import { connect } from "react-redux";
import {
  Col,
  InputGroup,
  Input,
  InputGroupText,
  Button,
  UncontrolledAlert,
} from "reactstrap";
import withStyles from "isomorphic-style-loader/withStyles";
import classNames from "classnames";
import moment from "moment-timezone";
import isEqual from "lodash.isequal";
import { ScaleLoader } from "react-spinners";
import ReactTooltip from "react-tooltip";

import {
  serviceCaller,
  ServiceCallerProps,
  subscribeToResizeChanges,
  withLoginRedirect,
} from "../../composition";

import {
  messengerDaySeparatorLabel,
  phonePlainFormat,
} from "../../../utils/helpers";
import {
  IChat,
  IMessage,
  canBeForwarded,
  ErrorCode,
} from "../../../types/IMessage";
import MessageBubble from "../messageBubble";
import DonorChatTitle from "../donorChatTitle";
import { sendMessage } from "../../../utils/fetch";
import { ISendMessageRequest } from "../../../types/services/ISendMessage";

import s from "./styles.scss";
import { isCurrentUserOnlyReader } from "../../../utils/roleCheck";

interface Props extends ServiceCallerProps {
  chat: IChat | null;
  chatLoading?: boolean;
  back: () => void;
  onCloseChat: () => void;
  mobileMode: boolean;
  userId: string;
  showDonorDetail: (id: string) => void;
  threadUnread?: boolean;
  inboxBusy?: boolean;
  onMarkUnread?: () => void;
}

interface State {
  newMessageText: string;
  recentlySent: Array<IMessage>;
  messagesInProgress: Array<IMessage>;
}

class Chat extends React.PureComponent<Props, State> {
  private MAX_LENGTH_OF_MESSAGE = 160;

  state = {
    newMessageText: "",
    recentlySent: [],
    messagesInProgress: [],
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { chat } = prevProps;

    if (this.props.chat !== prevProps.chat) {
      this.setState({
        recentlySent: [],
        messagesInProgress: [],
      });
    }

    const chatChanged = !isEqual(chat, this.props.chat);
    const prevThread =
      (prevProps.chat?.messageList?.length ?? 0) +
      prevState.recentlySent.length +
      prevState.messagesInProgress.length;
    const currThread =
      (this.props.chat?.messageList?.length ?? 0) +
      this.state.recentlySent.length +
      this.state.messagesInProgress.length;
    const threadCountChanged = currThread !== prevThread;

    if (chatChanged || threadCountChanged) {
      this.scrollChatToBottom();
    }
  }

  handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.prepareMessage();
    }
  };

  messageTextChange = e => {
    if (e.target.value.toString().length <= this.MAX_LENGTH_OF_MESSAGE) {
      this.setState({
        newMessageText: e.target.value,
      });
    }
  };

  resend = (message: IMessage): void => {
    if (
      canBeForwarded(message) &&
      !isCurrentUserOnlyReader(this.props.currentCharity)
    ) {
      this.submit(message);
    }
  };

  prepareMessage = (): void => {
    if (this.state.newMessageText !== "") {
      const {
        chat: { donor },
        userId,
      } = this.props;

      const newMessage: IMessage = {
        content: this.state.newMessageText,
        sentFromCharity: true,
        senderId: userId,
        donorId: donor.id,
        sent: true,
        date: Number(moment().format("x")),
      };

      this.submit(newMessage);
    }
  };

  submit = (message: IMessage): void => {
    const {
      chat: { donor },
    } = this.props;

    const toSend: ISendMessageRequest = {
      message: message,
      donorPhone: phonePlainFormat(donor.phoneNumber),
    };

    this.setState({
      messagesInProgress: this.state.messagesInProgress.concat([
        {
          ...message,
          sent: false,
          sending: true,
          error: null,
        },
      ]),
    });

    this.sendMessage(toSend);
  };

  sendMessage = (message: ISendMessageRequest): void => {
    this.setState({
      newMessageText: "",
    });

    sendMessage(
      this.props.config.notificationServiceUrl,
      this.props.currentCharity.id,
      this.props.serviceAccessToken,
      message
    )
      .then(_ => {
        this.setState({
          recentlySent: this.state.recentlySent.concat([message.message]),
          messagesInProgress: this.removeMessageFromInProgress(message.message),
        });
      })
      .catch(_ => {
        let failed = message.message;
        failed.sent = false;
        failed.sending = false;
        failed.error = ErrorCode.UNKNOWN_ERROR;

        this.setState({
          recentlySent: this.state.recentlySent.concat([failed]),
          messagesInProgress: this.removeMessageFromInProgress(message.message),
        });
      });
  };

  removeMessageFromInProgress = (message: IMessage): Array<IMessage> => {
    return this.state.messagesInProgress.filter(m => m.date !== message.date);
  };

  scrollChatToBottom = (): void => {
    if (typeof window === "undefined") {
      return;
    }
    const run = (): void => {
      const el = document.getElementById("chatscroll");
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    };
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        run();
        window.setTimeout(run, 0);
      });
    });
  };

  goToCreateViewSpark = (): void => {
    this.props.navigate("/viewsparks/new");
  };

  renderThreadBubbles = (chatsToShow: Array<IMessage>, chat: IChat) => {
    const { currentCharity } = this.props;
    const groups: Array<{
      key: string;
      dayLabel: string | null;
      bubbles: Array<React.ReactNode>;
    }> = [];

    for (let index = 0; index < chatsToShow.length; index++) {
      const message = chatsToShow[index];
      let previousDate: number | undefined;
      if (index > 0) {
        previousDate = chatsToShow[index - 1].date;
      }
      const dayLabel = messengerDaySeparatorLabel(message.date, previousDate);

      let lastMessage = true;
      if (index + 1 < chatsToShow.length) {
        lastMessage =
          chatsToShow[index + 1].sentFromCharity !== message.sentFromCharity;
      }

      const bubble = (
        <MessageBubble
          key={"messageBubble_" + index}
          message={message}
          donor={chat.donor}
          charityName={currentCharity.name}
          onClick={this.resend}
          lastMessage={lastMessage}
        />
      );

      if (dayLabel || groups.length === 0) {
        groups.push({
          key: "dayGroup_" + index,
          dayLabel: dayLabel,
          bubbles: [bubble],
        });
      } else {
        groups[groups.length - 1].bubbles.push(bubble);
      }
    }

    const items = [];
    for (let g = 0; g < groups.length; g++) {
      const group = groups[g];
      const children = [];
      if (group.dayLabel) {
        children.push(
          <div
            key={group.key + "_sep"}
            className={s.daySeparator}
            role="separator"
          >
            <span className={s.daySeparatorLabel}>{group.dayLabel}</span>
          </div>
        );
      }
      for (let b = 0; b < group.bubbles.length; b++) {
        children.push(group.bubbles[b]);
      }
      items.push(
        <div key={group.key} className={s.dayGroup}>
          {children}
        </div>
      );
    }

    return items;
  };

  render() {
    const {
      chat,
      chatLoading,
      currentCharity,
      mobileMode,
      back,
      onCloseChat,
      showDonorDetail,
      threadUnread,
      inboxBusy,
      onMarkUnread,
    } = this.props;
    const markUnreadDisabled = !onMarkUnread || threadUnread || inboxBusy;
    let chatsToShow = [];

    if (chatLoading) {
      return (
        <Col xs={12} md={7} className={classNames(s.container, s.loadingPane)}>
          <div className={s.loadingSpinnerWrap} aria-busy="true" aria-live="polite">
            <ScaleLoader color="#1f65b1" loading={true} />
          </div>
        </Col>
      );
    }

    if (!chat) {
      return (
        <Col xs={12} md={7} className={classNames(s.container, s.emptyPane)}>
          {!mobileMode && (
            <div className={s.emptyStateWrap}>
              <p className={s.emptyState}>
                Select a conversation on the left to start chatting.
              </p>
              <p className={s.emptyState}>
                Do you want to start a new thread?{" "}
                <button
                  type="button"
                  className={s.emptyStateCta}
                  onClick={this.goToCreateViewSpark}
                >
                  Send a ViewSpark
                </button>{" "}
                and begin connecting with your donors now!
              </p>
            </div>
          )}
        </Col>
      );
    } else {
      chatsToShow = this.state.recentlySent
        ? chat.messageList.concat(this.state.recentlySent)
        : chat.messageList;

      chatsToShow = this.state.messagesInProgress
        ? chatsToShow.concat(this.state.messagesInProgress)
        : chatsToShow;
    }

    return (
      <Col xs={12} md={7} className={s.container}>
        {mobileMode && (
          <div className={s.title}>
            <Button className={s.backButton} onClick={back}>
              <i className="fas fa-chevron-left" />
            </Button>
            <DonorChatTitle
              donor={chat.donor}
              showDonorDetail={showDonorDetail}
              variant="mobileToolbar"
            />
            <button
              type="button"
              className={s.markUnreadButton}
              onClick={onMarkUnread}
              disabled={markUnreadDisabled}
              aria-label="Mark as unread"
              data-tip
              data-for="messenger-mark-unread-mobile"
            >
              <i
                className={classNames("fa fa-envelope", s.markUnreadIcon)}
                aria-hidden={true}
              />
            </button>
            <ReactTooltip
              id="messenger-mark-unread-mobile"
              type="info"
              place="bottom"
              effect="float"
            >
              Mark as unread
            </ReactTooltip>
          </div>
        )}

        {!mobileMode && (
          <div className={s.threadHeaderBar}>
            <div className={s.threadTitleStretch}>
              <DonorChatTitle
                donor={chat.donor}
                showDonorDetail={showDonorDetail}
                variant="thread"
                embedInChatHeaderRow
              />
            </div>
            <button
              type="button"
              className={s.markUnreadButton}
              onClick={onMarkUnread}
              disabled={markUnreadDisabled}
              aria-label="Mark as unread"
              data-tip
              data-for="messenger-mark-unread"
            >
              <i
                className={classNames("fa fa-envelope", s.markUnreadIcon)}
                aria-hidden={true}
              />
            </button>
            <ReactTooltip
              id="messenger-mark-unread"
              type="info"
              place="bottom"
              effect="float"
            >
              Mark as unread
            </ReactTooltip>
            <button
              type="button"
              className={s.closeChatButton}
              onClick={onCloseChat}
              aria-label="Close conversation"
              data-tip
              data-for="messenger-close-chat"
            >
              <i
                className={classNames("fa fa-times", s.closeChatIcon)}
                aria-hidden={true}
              />
            </button>
            <ReactTooltip
              id="messenger-close-chat"
              type="info"
              place="bottom"
              effect="float"
            >
              Close chat
            </ReactTooltip>
          </div>
        )}

        <div className={s.bubblesContainer} id="chatscroll">
          {this.renderThreadBubbles(chatsToShow, chat)}
        </div>
        <div className={s.absoluteDiv}>
          {this.state.newMessageText.length >= this.MAX_LENGTH_OF_MESSAGE && (
            <UncontrolledAlert color="warning" className={s.noMarginAlert}>
              You have reached the characters limit for a text message.
            </UncontrolledAlert>
          )}
          <InputGroup className={classNames(s.inputSection)}>
            <Input
              id="messageText"
              type="textarea"
              name="messageText"
              placeholder={"Message..."}
              disabled={isCurrentUserOnlyReader(this.props.currentCharity)}
              value={this.state.newMessageText}
              onChange={this.messageTextChange}
              className={classNames(s.text, "h-100")}
              onKeyDown={this.handleKeyDown}
            />
            <InputGroupText>
              <div className={s.sendMessageDiv}>
                <i
                  className={s.sendMessageIcon}
                  onClick={this.prepareMessage}
                />
              </div>
            </InputGroupText>
          </InputGroup>
        </div>
      </Col>
    );
  }
}

const mapStateToProps = state => {
  const { user } = state;
  return {
    userId: user.id,
  };
};

export default serviceCaller(
  withLoginRedirect(
    connect<Props>(mapStateToProps)(
      withStyles(s)(subscribeToResizeChanges(Chat))
    )
  )
);
